import { collection, getDocs, limit, orderBy, query, startAfter, where } from "firebase/firestore";
import { auth, db } from "../config/firebase";
import { applyUserScope, scopeWhere } from "./tenancy";

/**
 * Ownership model shared with the mobile app: records are owned by the
 * signed-in user through `userId` (files, folders, notifications, audit logs,
 * chat) or `createdBy` (every other business collection).
 */
const USER_ID_COLLECTIONS = new Set([
  "files",
  "folders",
  "notifications",
  "auditLogs",
  "chatMessages",
  "chatSessions"
]);

/** Collections that are global, admin-only or keyed by uid; never owner-scoped. */
const UNSCOPED_COLLECTIONS = new Set([
  "users",
  "roles",
  "licenses",
  "AdminLicenses",
  "drpackLicenses",
  "LicenceSerialCode",
  "licenseLookupLogs",
  "loginLogs",
  "usage",
  "sessionPolicies",
  "exchangeRates",
  "mail",
  "privacyRequests",
  "workspaces",
  "companies",
  "masterDataItems",
  "versions",
  "legalEntities",
  "branches",
  "userScopes",
  "groupUsage",
  "supportAccessGrants",
  "accessEvents",
  "tenantDeletionRequests",
  "tenantDeletionCertificates"
]);

export function currentUid() {
  return auth?.currentUser?.uid || null;
}

export function ownerField(collectionName) {
  return USER_ID_COLLECTIONS.has(collectionName) ? "userId" : "createdBy";
}

export function isOwnerScoped(collectionName) {
  return !!collectionName && !UNSCOPED_COLLECTIONS.has(collectionName);
}

export function ownerStamp(uid = currentUid()) {
  return uid ? { userId: uid, createdBy: uid, ownerId: uid, tenantId: uid } : {};
}

/** Milliseconds for Firestore Timestamps, Dates, ISO strings, numbers or `{ seconds }`. */
export function toMillis(value) {
  if (value == null || value === "") return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

function compareValues(a, b) {
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "string" && typeof b === "string" && Number.isNaN(Date.parse(a))) {
    return a.localeCompare(b);
  }
  return toMillis(a) - toMillis(b);
}

export function sortRows(rows, field = "createdAt", direction = "desc") {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((x, y) => sign * compareValues(x[field] ?? null, y[field] ?? null));
}

/**
 * Lists the signed-in user's documents in `collectionName`.
 * Uses equality filters only (served by single-field indexes) and sorts on the
 * client, so no composite index is required.
 */
export async function listOwned(
  collectionName,
  { filters = [], sort = "createdAt", direction = "desc", max = null, includeDeleted = false, uid = currentUid() } = {}
) {
  if (!uid) return [];
  const constraints = [scopeWhere(collectionName, ownerField(collectionName), uid, currentUid())];
  filters.forEach(([field, value]) => constraints.push(where(field, "==", value)));
  const snap = await getDocs(query(collection(db, collectionName), ...constraints));
  let rows = applyUserScope(collectionName, snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  if (!includeDeleted) rows = rows.filter((r) => !r.deletedAt);
  rows = sortRows(rows, sort, direction);
  return max ? rows.slice(0, max) : rows;
}

export const PAGE_SIZE = 50;

const isMissingIndex = (err) =>
err?.code === "failed-precondition" || /requires an index/i.test(String(err?.message || ""));

/**
 * One page of the signed-in user's documents, ordered on the server
 * (`orderBy` + `limit` + `startAfter`); needs the matching composite index in
 * firestore.indexes.json. Documents without the sort field are not returned,
 * hence the createdAt backfill migration. While an index is still building the
 * call falls back to the client-sorted full read, paged by offset.
 */
export async function listOwnedPage(
  collectionName,
  { filters = [], sort = "createdAt", direction = "desc", pageSize = PAGE_SIZE, cursor = null, includeDeleted = false, uid = currentUid() } = {}
) {
  if (!uid) return { rows: [], cursor: null, hasMore: false };
  const base = [scopeWhere(collectionName, ownerField(collectionName), uid, currentUid())];
  filters.forEach(([field, value]) => base.push(where(field, "==", value)));

  if (!cursor || cursor.offset === undefined) {
    try {
      const constraints = [...base, orderBy(sort, direction)];
      if (cursor) constraints.push(startAfter(cursor));
      constraints.push(limit(pageSize));
      const snap = await getDocs(query(collection(db, collectionName), ...constraints));
      let rows = applyUserScope(collectionName, snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      if (!includeDeleted) rows = rows.filter((r) => !r.deletedAt);
      const hasMore = snap.docs.length === pageSize;
      return { rows, cursor: hasMore ? snap.docs[snap.docs.length - 1] : null, hasMore };
    } catch (err) {
      if (!isMissingIndex(err) || cursor) throw err;
    }
  }

  const offset = cursor?.offset || 0;
  const all = await listOwned(collectionName, { filters, sort, direction, includeDeleted, uid });
  const rows = all.slice(offset, offset + pageSize);
  const hasMore = offset + pageSize < all.length;
  return { rows, cursor: hasMore ? { offset: offset + pageSize } : null, hasMore };
}
